// Convert hex -> RGB
export const getLogoColorFilters = (color) => {
  return color == '#076698'
    ? ''
    : 'invert(100%) sepia(100%) saturate(0%) hue-rotate(275deg) brightness(104%) contrast(105%)'
}
export const getImageColorFilters = (color) => {
  if (color == '#056553')
    return 'invert(27%) sepia(86%) saturate(408%) hue-rotate(119deg) brightness(98%) contrast(101%)'
  if (color == '#8B16AE')
    return 'invert(15%) sepia(92%) saturate(4281%) hue-rotate(282deg) brightness(78%) contrast(97%)'
  if (color == '#E98D32')
    return 'invert(16%) sepia(64%) saturate(4456%) hue-rotate(280deg) brightness(90%) contrast(102%)'
  if (color == '#931013')
    return 'invert(9%) sepia(107%) saturate(7117%) hue-rotate(353deg) brightness(81%) contrast(93%)'
  if (color == '#202020')
    return 'invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(12%) contrast(100%)'
  if (color == '#7B2CBF')
    return 'invert(18%) sepia(88%) saturate(3500%) hue-rotate(270deg) brightness(76%) contrast(95%)'
  if (color == '#F9C80E')
    return 'invert(85%) sepia(100%) saturate(2500%) hue-rotate(355deg) brightness(98%) contrast(100%)'
  if (color == '#D7DF23')
    return 'invert(90%) sepia(80%) saturate(2000%) hue-rotate(38deg) brightness(90%) contrast(102%)'
  if (color == '#EA3546')
    return 'invert(20%) sepia(90%) saturate(5500%) hue-rotate(340deg) brightness(92%) contrast(100%)'
  if (color == '#43BCCD')
    return 'invert(72%) sepia(55%) saturate(900%) hue-rotate(148deg) brightness(88%) contrast(102%)'
  return ''
}

const hexToRgb = (hex) => {
  const cleanHex = hex.replace(/^#/, '')
  const bigint = parseInt(cleanHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return { r, g, b }
}

const rgbToHex = (r, g, b) =>
  '#' +
  [r, g, b]
    .map((x) => {
      const hex = x.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    })
    .join('')

// Lighten/darken 
export const adjustColor = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex)
  const newR = Math.min(255, Math.max(0, r + (r * percent) / 100))
  const newG = Math.min(255, Math.max(0, g + (g * percent) / 100))
  const newB = Math.min(255, Math.max(0, b + (b * percent) / 100))
  return rgbToHex(Math.round(newR), Math.round(newG), Math.round(newB))
}

export const getVariants = (hex) => {
  if (!hex) return { light: null, dark: null }
  return {
    light: adjustColor(hex, 40),
    dark: adjustColor(hex, -30),
    extraDark: adjustColor(hex, -50),
    lighter: adjustColor(hex, 600),
  }
}

// ── WCAG contrast utilities ──────────────────────────────────────────

/** WCAG 2.1 relative luminance (sRGB). */
const relativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex)
  const c = (v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b)
}

/**
 * WCAG contrast ratio between two hex colors.
 * Order doesn't matter — returns the same value either way.
 */
export const contrastRatio = (hex1, hex2) => {
  const l1 = relativeLuminance(hex1)
  const l2 = relativeLuminance(hex2)
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

/**
 * Ensure `color` has at least `minContrast` against `background`.
 * If it doesn't, darken (if bg is light) or lighten (if bg is dark)
 * until it meets the threshold or we hit the clamp.
 *
 * @param {string} color      - hex color to adjust
 * @param {string} background - hex background to compare against
 * @param {number} minContrast - target WCAG ratio (default 4.5)
 * @returns {string} adjusted hex color
 */
export const ensureContrast = (color, background, minContrast = 4.5) => {
  if (!color || !background) return color

  const bgLum = relativeLuminance(background)
  const fgLum = relativeLuminance(color)
  const current = contrastRatio(color, background)

  if (current >= minContrast) return color

  // bg is light → darken the foreground
  // bg is dark  → lighten the foreground
  const darken = bgLum > 0.5
  const direction = darken ? -1 : 1

  // Step by 3% until we reach minContrast or 50 steps
  let adjusted = color
  for (let i = 0; i < 50; i++) {
    adjusted = adjustColor(adjusted, direction * 3)
    if (contrastRatio(adjusted, background) >= minContrast) return adjusted
  }

  // Hard fallback — if bg is light, return near-black; if dark, near-white
  return darken ? '#1f2937' : '#f8f9fa'
}

/**
 * Ensure two colors are distinguishable (for hover/active states).
 * Darkens or lightens `color` until it has ≥ `minContrast` against `background`.
 *
 * @param {string} color      - hex color to adjust (e.g. the hover state)
 * @param {string} background - the surrounding background
 * @param {number} minContrast - minimum contrast between the two (default 2.5)
 * @returns {string} adjusted hex color
 */
export const ensureDistinguishable = (color, background, minContrast = 2.5) => {
  if (!color || !background) return color

  const current = contrastRatio(color, background)
  if (current >= minContrast) return color

  const bgLum = relativeLuminance(background)
  const fgLum = relativeLuminance(color)

  // If color is already darker than bg and we need more contrast, go darker.
  // If lighter, go lighter.
  const darken = fgLum >= bgLum ? false : true
  const direction = darken ? -1 : 1

  let adjusted = color
  for (let i = 0; i < 50; i++) {
    adjusted = adjustColor(adjusted, direction * 3)
    if (contrastRatio(adjusted, background) >= minContrast) return adjusted
  }

  return darken ? '#000000' : '#ffffff'
}
